import { useTranslation } from "react-i18next";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { ToolAccess } from "@/lib/api";
import { ACCESS_CHIP, CHIP_FOCUS } from "@/lib/chips";
import { ROW_CLICK_THROUGH } from "@/lib/row-click";
import { cn } from "@/lib/utils";

interface AccessLetterProps {
  access: ToolAccess;
  className?: string;
}

/**
 * Whether a tool reads or writes, as a small circled letter in the colours of
 * the Lezen and Schrijven chips (reading in De Ceuster's light blue tint,
 * writing in solid De Ceuster blue): L or S in Dutch, R or W in English, L or
 * É in French. The word itself shows on hover and on keyboard focus, and is
 * the letter's name for screen readers. A tooltip never opens on touch, so a
 * click on the letter opens the tool's dialog, as a click on its row does
 * (`ROW_CLICK_THROUGH`), where the word is written out.
 */
export function AccessLetter({ access, className }: AccessLetterProps) {
  const { t } = useTranslation();
  const word = t(`tools.access.${access}`);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          aria-label={word}
          tabIndex={0}
          {...ROW_CLICK_THROUGH}
          className={cn(
            "inline-flex size-4 shrink-0 items-center justify-center self-center rounded-full text-[0.6rem] leading-none font-semibold",
            ACCESS_CHIP[access],
            CHIP_FOCUS,
            className,
          )}
        >
          <span aria-hidden="true">{t(`tools.accessLetter.${access}`)}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{word}</TooltipContent>
    </Tooltip>
  );
}
